import { useState, useEffect } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { useSocket } from '@/lib/socket';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';


import { MoveLocationDialog } from './MoveLocationDialog';
import { useLocationContext } from '@/contexts/LocationContext';
import { Package } from 'lucide-react';

interface WorkOrder {
  id: string;
  orderID: string;
  index: string;
  fabricType: string;
  frameColorVaries: boolean;
  frameColor: string;
  buildoutFrameColor: string;
  hemBarFrameColor: string;
  housingFrameColor: string;
  retentionFrameColor: string;
  kittingComplete: boolean;
  fabricComplete: boolean;
  metalComplete: boolean;
  finalAssemblyComplete: boolean;
  isTKS0026?: boolean;
}

interface OtherItem {
  displayName: string;
  quantity: number;
  details: string;
  index: string;
  isReady: boolean;
}

interface SalesOrder {
  id: string;
  orderNumber: string;
  date: string;
  customer: string;
  location: string;
  workOrders: WorkOrder[];
  otherItems: OtherItem[];
}

interface KanbanItem {
  id: string;
  orderID: string;
  customer: string;
  location: string;
  status: 'queue' | 'in-process' | 'done (not shipped)';
  baseOrderNumber: string;
  fabricType?: string;
  frameColor?: string;
  frameColorVaries?: boolean;
  housingFrameColor?: string;
  buildoutFrameColor?: string;
  hemBarFrameColor?: string;
  retentionFrameColor?: string;
  kittingComplete: boolean;
  fabricComplete: boolean;
  metalComplete: boolean;
  finalAssemblyComplete: boolean;
  isWorkOrder: boolean;
  displayName?: string;
  quantity?: number;
  details?: string;
  isTKS0026?: boolean;
}

interface GroupedOrder {
  baseOrderNumber: string;
  items: KanbanItem[];
  customer: string;
  location: string;
  status: 'queue' | 'in-process' | 'done (not shipped)';
}

export function KanbanView() {
  const socket = useSocket();
  const [orders, setOrders] = useState<GroupedOrder[]>([]);
  const { locations } = useLocationContext();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<{
    orderNumber: string;
    location: string;
  } | null>(null);
  const [stateIcons, setStateIcons] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const loadStateIcons = async () => {
      const icons: { [key: string]: string } = {};
      
      for (const location of locations) {
        try {
          // Use URL constructor to ensure proper path resolution
          const iconUrl = new URL(`/states/${location.state}.svg`, window.location.origin).href;
          // Verify the SVG exists
          const response = await fetch(iconUrl);
          if (response.ok) {
            icons[location.state] = iconUrl;
          } else {
            console.warn(`SVG not found for ${location.state}`);
          }
        } catch (error) {
          console.error(`Failed to load SVG for ${location.state}:`, error);
        }
      }
      setStateIcons(icons);
    };

    loadStateIcons();
  }, [locations]);

  useEffect(() => {
    if (socket) {
      let updateTimeout: NodeJS.Timeout;
      
      const debouncedHandleNetSuiteData = (data: SalesOrder[]) => {
        clearTimeout(updateTimeout);
        updateTimeout = setTimeout(() => {
          handleNetSuiteData(data);
        }, 100); // Debounce socket updates by 100ms
      };
      
            const handleNetSuiteData = (data: SalesOrder[]) => {
        // Use requestAnimationFrame to defer heavy processing
        requestAnimationFrame(() => {
          const groupedData: { [key: string]: GroupedOrder } = {};
          
          if (Array.isArray(data)) {
            // Iterate through each sales order
            data.forEach(salesOrder => {
              const baseOrderNumber = salesOrder.orderNumber;
              const items: KanbanItem[] = [];
              
              // Process work orders for this sales order
              if (Array.isArray(salesOrder.workOrders)) {
                salesOrder.workOrders.forEach((wo: WorkOrder) => {
                  items.push({
                    id: wo.id,
                    orderID: wo.orderID,
                    customer: salesOrder.customer,
                    location: salesOrder.location || 'No Location',
                    status: 'queue',
                    baseOrderNumber,
                    fabricType: wo.fabricType,
                    frameColor: wo.frameColor,
                    frameColorVaries: wo.frameColorVaries,
                    housingFrameColor: wo.housingFrameColor,
                    buildoutFrameColor: wo.buildoutFrameColor,
                    hemBarFrameColor: wo.hemBarFrameColor,
                    retentionFrameColor: wo.retentionFrameColor,
                    kittingComplete: wo.kittingComplete,
                    fabricComplete: wo.fabricComplete,
                    metalComplete: wo.metalComplete,
                    finalAssemblyComplete: wo.finalAssemblyComplete,
                    isWorkOrder: true,
                    isTKS0026: wo.isTKS0026
                  });
                });
              }
              
              // Process other items for this sales order
              if (Array.isArray(salesOrder.otherItems)) {
                salesOrder.otherItems.forEach((item: OtherItem) => {
                  items.push({
                    id: `${salesOrder.id}-${item.index}`,
                    orderID: salesOrder.orderNumber,
                    customer: salesOrder.customer,
                    location: salesOrder.location || 'No Location',
                    status: 'queue',
                    baseOrderNumber,
                    kittingComplete: item.isReady,
                    fabricComplete: item.isReady,
                    metalComplete: item.isReady,
                    finalAssemblyComplete: item.isReady,
                    isWorkOrder: false,
                    displayName: item.displayName,
                    quantity: item.quantity,
                    details: item.details
                  });
                });
              }
              
              if (items.length > 0) {
                groupedData[baseOrderNumber] = {
                  baseOrderNumber,
                  customer: salesOrder.customer,
                  location: salesOrder.location || 'No Location',
                  items,
                  status: 'queue'
                };
                
                const allItemsComplete = items.every(item => {
                  // For TKS-0026 items, only check metal and final assembly completion
                  if (item.isTKS0026) {
                    return item.metalComplete && item.finalAssemblyComplete;
                  }
                  // For regular items, check all four phases
                  return item.kittingComplete && 
                         item.fabricComplete && 
                         item.metalComplete && 
                         item.finalAssemblyComplete;
                });

                const anyItemStarted = items.some(item => {
                  if (item.isTKS0026) {
                    return item.metalComplete || item.finalAssemblyComplete;
                  }
                  return item.kittingComplete || 
                         item.fabricComplete || 
                         item.metalComplete || 
                         item.finalAssemblyComplete;
                });

                if (allItemsComplete) {
                  groupedData[baseOrderNumber].status = 'done (not shipped)';
                } else if (anyItemStarted) {
                  groupedData[baseOrderNumber].status = 'in-process';
                }

              groupedData[baseOrderNumber].items = items.map(item => ({
                ...item,
                status: groupedData[baseOrderNumber].status
              }));
            }
          });
        }

        setOrders(Object.values(groupedData));
        setIsLoading(false);
      });
    };

      socket.on('connect', () => {
        toast({
          title: 'Connected',
          description: 'Connected to server successfully',
        });
        socket.emit('requestNetSuiteData');
      });

      socket.on('disconnect', () => {
        toast({
          title: 'Disconnected',
          description: 'Lost connection to server',
          variant: 'destructive',
        });
      });

      socket.on('netsuiteData', debouncedHandleNetSuiteData);
      socket.on('netsuiteError', (error) => {
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        });
        setIsLoading(false);
      });

      socket.on('orderMoved', (data) => {
        setOrders(prevOrders => {
          const updatedOrders = prevOrders.map(order => {
            if (order.baseOrderNumber === data.salesOrderNumber) {
              return {
                ...order,
                location: data.location,
                items: order.items.map(item => ({
                  ...item,
                  location: data.location
                }))
              };
            }
            return order;
          });
          return updatedOrders;
        });
      });

      const loadingTimeout = setTimeout(() => {
        if (isLoading) {
          setIsLoading(false);
          toast({
            title: 'Error',
            description: 'Failed to load data. Please try refreshing the page.',
            variant: 'destructive',
          });
        }
      }, 10000);

      return () => {
        socket.off('netsuiteData', debouncedHandleNetSuiteData);
        socket.off('netsuiteError');
        socket.off('connect');
        socket.off('disconnect');
        socket.off('orderMoved');
        clearTimeout(loadingTimeout);
        clearTimeout(updateTimeout);
      };
    }
  }, [socket, toast, isLoading]);

  const filteredLocations = locations
    .filter(loc => loc.state !== 'USA')
    .map(loc => `${loc.city} ${loc.state}`);
  
  const allLocations = [
    'No Location',
    ...filteredLocations.map(loc => loc.replace(', ', ' '))
  ];

  const locationsWithOrders = allLocations.filter(location => 
    orders.some(order => order.location === location)
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'queue': return 'bg-gray-100';
      case 'in-process': return 'bg-blue-50';
      case 'done (not shipped)': return 'bg-green-50';
      default: return 'bg-white';
    }
  };

  const getStateColor = (location: string) => {
    const state = location.split(' ').pop();
    switch (state) {
      case 'TX':
        return 'from-red-500 to-orange-600';
      case 'WA':
        return 'from-emerald-500 to-teal-600';
      case 'USA':
        return 'from-yellow-400 to-orange-400';
      case 'CA':
        return 'from-purple-500 to-pink-600';
      case 'FL':
        return 'from-cyan-500 to-blue-600';
      case 'NY':
        return 'from-slate-600 to-gray-700';
      case 'IL':
        return 'from-amber-500 to-yellow-600';
      case 'PA':
        return 'from-rose-500 to-red-600';
      case 'OH':
        return 'from-green-500 to-emerald-600';
      case 'GA':
        return 'from-orange-500 to-red-600';
      case 'NC':
        return 'from-teal-500 to-cyan-600';
      case 'MI':
        return 'from-violet-500 to-purple-600';
      case 'NJ':
        return 'from-indigo-500 to-blue-600';
      case 'VA':
        return 'from-pink-500 to-rose-600';
      case 'TN':
        return 'from-lime-500 to-green-600';
      case 'IN':
        return 'from-yellow-500 to-amber-600';
      case 'AZ':
        return 'from-orange-500 to-amber-600';
      case 'MA':
        return 'from-blue-600 to-indigo-700';
      case 'WI':
        return 'from-green-600 to-teal-700';
      case 'MN':
        return 'from-blue-500 to-purple-600';
      case 'CO':
        return 'from-sky-500 to-blue-600';
      case 'AL':
        return 'from-red-500 to-pink-600';
      case 'LA':
        return 'from-purple-600 to-indigo-700';
      case 'KY':
        return 'from-emerald-600 to-green-700';
      case 'OR':
        return 'from-teal-500 to-emerald-600';
      case 'OK':
        return 'from-orange-600 to-red-700';
      case 'CT':
        return 'from-blue-400 to-cyan-600';
      case 'UT':
        return 'from-slate-500 to-gray-600';
      case 'NV':
        return 'from-yellow-600 to-orange-700';
      case 'AR':
        return 'from-red-600 to-rose-700';
      case 'MS':
        return 'from-indigo-600 to-purple-700';
      case 'KS':
        return 'from-amber-600 to-yellow-700';
      case 'NM':
        return 'from-orange-400 to-red-500';
      case 'NE':
        return 'from-blue-300 to-blue-500';
      case 'WV':
        return 'from-green-400 to-emerald-500';
      case 'ID':
        return 'from-purple-400 to-indigo-500';
      case 'HI':
        return 'from-cyan-400 to-teal-500';
      case 'ME':
        return 'from-blue-600 to-slate-700';
      case 'NH':
        return 'from-green-500 to-blue-500';
      case 'RI':
        return 'from-blue-500 to-indigo-500';
      case 'MT':
        return 'from-slate-400 to-gray-500';
      case 'DE':
        return 'from-blue-400 to-indigo-400';
      case 'SD':
        return 'from-yellow-400 to-orange-500';
      case 'ND':
        return 'from-amber-400 to-yellow-500';
      case 'AK':
        return 'from-cyan-600 to-blue-700';
      case 'VT':
        return 'from-green-600 to-emerald-700';
      case 'WY':
        return 'from-orange-300 to-amber-400';
      case 'DC':
        return 'from-red-600 to-pink-700';
      default:
        return 'from-gray-500 to-slate-600';
    }
  };

  const getStateFromLocation = (location: string) => {
    return location.split(' ').pop() || '';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] animate-fade-in">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 mx-auto"></div>
          </div>
          <h3 className="mt-6 text-xl font-semibold text-slate-700">Loading Production Data</h3>
          <p className="mt-2 text-slate-500">Fetching the latest order information...</p>
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] animate-fade-in">
        <div className="text-center max-w-md">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
            <Package className="w-10 h-10 text-blue-500" />
          </div>
          <h3 className="text-2xl font-semibold text-slate-700 mb-3">No Orders Found</h3>
          <p className="text-slate-500 text-lg leading-relaxed">
            There are no orders to display at this time. New orders will appear here when they become available.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in">
        {locationsWithOrders.map((location) => (
          <Card key={location} className="group overflow-hidden border-0 shadow-md hover:shadow-lg transition-all duration-200 hover:-translate-y-1 bg-white/95">
            <CardHeader className={`py-4 bg-gradient-to-r ${getStateColor(location)} relative`}>
              
              <div className="flex items-center justify-between relative z-10">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    {stateIcons[getStateFromLocation(location)] ? (
                      <img 
                        src={stateIcons[getStateFromLocation(location)]} 
                        alt={getStateFromLocation(location)}
                        className="w-6 h-6 drop-shadow-sm" 
                      />
                    ) : (
                      <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm">📍</span>
                      </div>
                    )}
                  </div>
                  <CardTitle className="text-lg font-bold text-white tracking-wide drop-shadow-sm">
                    {location}
                  </CardTitle>
                </div>
                <Badge className="bg-white/90 text-slate-700 font-semibold px-3 py-1">
                  {orders.filter(order => order.location === location).length}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
                {orders
                  .filter((order) => order.location === location)
                  .sort((a, b) => {
                    // First sort by status: queue (0), in-process (1), done (2)
                    const statusOrder = { 'queue': 0, 'in-process': 1, 'done (not shipped)': 2 };
                    const statusComparison = statusOrder[a.status] - statusOrder[b.status];
                    if (statusComparison !== 0) return statusComparison;
                    
                    // Then by order number within each status group
                    return a.baseOrderNumber.localeCompare(b.baseOrderNumber);
                  })
                  .map((order) => (
                  <div
                    key={order.baseOrderNumber}
                    className={`p-4 rounded-lg border border-slate-200 ${getStatusColor(order.status)} 
                      hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer`}
                    onClick={() => setSelectedOrder({ orderNumber: order.baseOrderNumber, location: order.location })}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h4 className="font-bold text-slate-800 text-lg tracking-tight">
                          #{order.baseOrderNumber}
                        </h4>
                        <p className="text-slate-600 font-medium mt-1">{order.customer}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge
                          className={`text-xs font-medium px-2 py-1 rounded-lg border-0 shadow-sm ${
                            order.status === 'queue' ? 'bg-slate-100 text-slate-700' :
                            order.status === 'in-process' ? 'bg-blue-100 text-blue-700' :
                            'bg-emerald-100 text-emerald-700'
                          }`}
                        >
                          {order.status === 'done (not shipped)' ? 'Complete' : 
                           order.status === 'in-process' ? 'In Progress' : 'Queued'}
                        </Badge>
                      </div>
                    </div>

                    {/* Progress indicators */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600 font-medium">Progress</span>
                        <span className="text-slate-700 font-semibold">{order.items.length} items</span>
                      </div>
                      
                      {/* Stage progress dots */}
                      <div className="flex items-center gap-3 bg-white/60 rounded-lg p-3">
                        <div className="flex items-center gap-1.5">
                          <div className={`w-3 h-3 rounded-full transition-all duration-100 ${
                            order.items.every(i => i.kittingComplete) ? 'bg-emerald-500' : 'bg-slate-300'
                          }`} />
                          <span className="text-xs font-medium text-slate-600">Kit</span>
                        </div>
                        
                        <div className="flex items-center gap-1.5">
                          <div className={`w-3 h-3 rounded-full transition-all duration-100 ${
                            order.items.every(i => i.fabricComplete) ? 'bg-purple-500' : 'bg-slate-300'
                          }`} />
                          <span className="text-xs font-medium text-slate-600">Fabric</span>
                        </div>
                        
                        <div className="flex items-center gap-1.5">
                          <div className={`w-3 h-3 rounded-full transition-all duration-100 ${
                            order.items.every(i => i.metalComplete) ? 'bg-amber-500' : 'bg-slate-300'
                          }`} />
                          <span className="text-xs font-medium text-slate-600">Metal</span>
                        </div>
                        
                        <div className="flex items-center gap-1.5">
                          <div className={`w-3 h-3 rounded-full transition-all duration-100 ${
                            order.items.every(i => i.finalAssemblyComplete) ? 'bg-blue-500' : 'bg-slate-300'
                          }`} />
                          <span className="text-xs font-medium text-slate-600">Assembly</span>
                        </div>
                      </div>

                      {/* Individual items list */}
                      <div className="space-y-2 max-h-32 overflow-y-auto custom-scrollbar">
                        {order.items.map((item, index) => (
                          <div key={index} className={`text-xs p-2 rounded-lg transition-all duration-100 ${
                            item.isWorkOrder && item.isTKS0026 ? 'bg-amber-50 border border-amber-200' :
                            item.isWorkOrder ? 'bg-blue-50 border border-blue-200' : 
                            'bg-slate-50 border border-slate-200'
                          }`}>
                            <div className="flex justify-between items-center">
                              <span className="font-medium text-slate-700 truncate">
                                {item.isWorkOrder 
                                  ? (item.isTKS0026 ? `Buildout: ${item.orderID}` : `Screen Unit: ${item.orderID}`)
                                  : `${item.displayName}${item.quantity && item.quantity > 1 ? ` (${item.quantity})` : ''}`
                                }
                              </span>
                              <div className="flex gap-1">
                                {!item.isWorkOrder ? (
                                  <div className={`w-1.5 h-1.5 rounded-full ${item.kittingComplete ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                                ) : item.isTKS0026 ? (
                                  <>
                                    <div className={`w-1.5 h-1.5 rounded-full ${item.metalComplete ? 'bg-amber-500' : 'bg-slate-300'}`} title="Metal" />
                                    <div className={`w-1.5 h-1.5 rounded-full ${item.finalAssemblyComplete ? 'bg-blue-500' : 'bg-slate-300'}`} title="Assembly" />
                                  </>
                                ) : (
                                  <>
                                    <div className={`w-1.5 h-1.5 rounded-full ${item.kittingComplete ? 'bg-emerald-500' : 'bg-slate-300'}`} title="Kitting" />
                                    <div className={`w-1.5 h-1.5 rounded-full ${item.fabricComplete ? 'bg-purple-500' : 'bg-slate-300'}`} title="Fabric" />
                                    <div className={`w-1.5 h-1.5 rounded-full ${item.metalComplete ? 'bg-amber-500' : 'bg-slate-300'}`} title="Metal" />
                                    <div className={`w-1.5 h-1.5 rounded-full ${item.finalAssemblyComplete ? 'bg-blue-500' : 'bg-slate-300'}`} title="Assembly" />
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
          </Card>
        ))}
      </div>

      <MoveLocationDialog
        isOpen={!!selectedOrder}
        onClose={() => setSelectedOrder(null)}
        orderNumber={selectedOrder?.orderNumber || ''}
        currentLocation={selectedOrder?.location || ''}
      />
    </>
  );
} 